import { CompanionCardHeader } from '@/components/companion/cards/CompanionCardHeader';
import { CompanionCardShell, EvidenceLine } from '@/components/companion/cards/CompanionCardShell';
import { formatCompanionDate } from '@/components/companion/feed/companion-formatters';
import { Button, Stack, Text } from '@chakra-ui/react';
import type { CompanionActionDto, CompanionGiftExpiredReofferEvidence } from '@fluenceur/types';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface GiftExpiredReofferCardProps {
  action: CompanionActionDto & { evidence: CompanionGiftExpiredReofferEvidence };
  onOpenCta: () => void;
  onDismiss?: () => void;
  isCtaPending?: boolean;
  isCtaDisabled?: boolean;
  flat?: boolean;
  footerEnd?: ReactNode;
}

/** A grouped, frozen expiry signal. It never derives targets from the current list. */
export const GiftExpiredReofferCard = ({
  action,
  onOpenCta,
  onDismiss,
  isCtaPending,
  isCtaDisabled,
  flat,
  footerEnd,
}: GiftExpiredReofferCardProps) => {
  const { t, i18n } = useTranslation();
  const { gifts } = action.evidence;
  const [expandedActionId, setExpandedActionId] = useState<string | null>(null);
  const isExpanded = expandedActionId === action.id;
  const evidenceId = useId();
  const remainingCount = Math.max(0, gifts.length - 3);
  const visibleGifts = isExpanded ? gifts : gifts.slice(0, 3);

  return (
    <CompanionCardShell
      testId="companion-gift-expired-reoffer-card"
      dataAttrs={{ 'data-gift-request-ids': gifts.map((gift) => gift.giftRequestId).join(',') }}
      flat={flat}
      ctaLabel={action.ctaLabel}
      onOpenCta={onOpenCta}
      onDismiss={onDismiss}
      isCtaPending={isCtaPending}
      isCtaDisabled={isCtaDisabled}
      footerEnd={footerEnd}
    >
      <CompanionCardHeader title={action.title} reason={action.reason}>
        <Text fontSize="13px" color="ink" lineHeight="1.45">
          {t('companion.cards.giftExpiredReoffer.count', { count: gifts.length })}
        </Text>
      </CompanionCardHeader>
      <Stack id={evidenceId} gap="2px" maxH="240px" overflowY="auto">
        {visibleGifts.map((gift) => (
          <EvidenceLine key={gift.giftRequestId}>
            {t('companion.cards.giftExpiredReoffer.item', {
              name: gift.displayName ?? t('companion.cards.giftExpiredReoffer.unknownAffiliate'),
              products:
                gift.productTitles.join(', ') ||
                t('companion.cards.giftExpiredReoffer.productsUnknown'),
              date: formatCompanionDate(gift.expiredAt, i18n.language),
            })}
          </EvidenceLine>
        ))}
      </Stack>
      {remainingCount > 0 ? (
        <Button
          variant="plain"
          size="sm"
          alignSelf="start"
          px="0"
          color="inkMuted"
          aria-expanded={isExpanded}
          aria-controls={evidenceId}
          onClick={() => setExpandedActionId(isExpanded ? null : action.id)}
        >
          {t(
            isExpanded
              ? 'companion.cards.giftExpiredReoffer.showLess'
              : 'companion.cards.giftExpiredReoffer.showMore',
            { count: remainingCount }
          )}
        </Button>
      ) : null}
    </CompanionCardShell>
  );
};
